import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarIcon, ClockIcon, AccountIcon as UserIcon, XIcon as X } from '../components/icons';
import { Users, MessageSquare, CheckCircle, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase/client';
import { collection, addDoc, query, where, getDocs, Timestamp } from 'firebase/firestore';
import NotificationService from '../services/NotificationService';
import { Restaurant, Reservation } from '../types';

interface ReservationModalProps {
    restaurant: Restaurant;
    onClose: () => void;
}

const ReservationModal: React.FC<ReservationModalProps> = ({ restaurant, onClose }) => {
    const { currentUser } = useAuth();
    const [step, setStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const [formData, setFormData] = useState({
        guests: 2,
        date: new Date().toISOString().split('T')[0],
        time: '19:30',
        specialRequests: '',
        clientName: currentUser?.name || '',
        clientPhone: currentUser?.phone || ''
    });

    const [capacityInfo, setCapacityInfo] = useState<{ current: number; max: number }>({ current: 0, max: restaurant.reservationSettings?.maxCapacityPerSlot || 100 });

    // Check capacity whenever date or time changes
    useEffect(() => {
        const checkCapacity = async () => {
            if (!restaurant.id || !formData.date || !formData.time) return;
            
            try {
                const selectedDate = new Date(formData.date);
                selectedDate.setHours(0, 0, 0, 0);
                
                const q = query(
                    collection(db, 'reservations'),
                    where('restaurantId', '==', restaurant.id),
                    where('date', '==', Timestamp.fromDate(selectedDate)),
                    where('status', 'in', ['pending', 'confirmed'])
                );
                
                const snapshot = await getDocs(q);
                const reservations = snapshot.docs.map(doc => doc.data() as Reservation);
                
                // Simple overlap check: for now, we just check reservations at the EXACT same time
                // or within the slot duration window.
                const slotDuration = restaurant.reservationSettings?.slotDurationMinutes || 90;
                const selectedTimeMinutes = timeToMinutes(formData.time);
                
                const overlappingGuests = reservations.reduce((sum, res) => {
                    const resTimeMinutes = timeToMinutes(res.time);
                    const diff = Math.abs(resTimeMinutes - selectedTimeMinutes);
                    if (diff < slotDuration) {
                        return sum + res.guests;
                    }
                    return sum;
                }, 0);
                
                setCapacityInfo({
                    current: overlappingGuests,
                    max: restaurant.reservationSettings?.maxCapacityPerSlot || 100
                });
            } catch (err) {
                console.error("Error checking capacity:", err);
            }
        };

        checkCapacity();
    }, [formData.date, formData.time, restaurant.id]);

    const timeToMinutes = (time: string) => {
        const [h, m] = time.split(':').map(Number);
        return h * 60 + m;
    };

    const handleNext = () => {
        if (step === 1) {
            // Validate first step
            if (capacityInfo.current + formData.guests > capacityInfo.max) {
                setError("Désolé, ce créneau est complet pour le nombre de personnes demandé.");
                return;
            }
            setError(null);
            setStep(2);
        }
    };

    const handleSubmit = async () => {
        if (!formData.clientName || !formData.clientPhone) {
            setError("Veuillez remplir vos informations de contact.");
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const selectedDate = new Date(formData.date);
            selectedDate.setHours(0, 0, 0, 0);

            await addDoc(collection(db, 'reservations'), {
                restaurantId: restaurant.id,
                restaurantName: restaurant.name,
                clientId: currentUser?.uid || 'anonymous',
                clientName: formData.clientName,
                clientPhone: formData.clientPhone,
                date: Timestamp.fromDate(selectedDate),
                time: formData.time,
                guests: formData.guests,
                specialRequests: formData.specialRequests,
                status: 'pending',
                createdAt: Timestamp.now()
            });

            // Send Notification to Restaurant Owner
            if (restaurant.ownerId) {
                NotificationService.sendToUser(restaurant.ownerId, {
                    title: "📅 Nouvelle réservation !",
                    body: `${currentUser?.name || formData.clientName} souhaite réserver pour ${formData.guests} personnes.`,
                    data: {
                        type: 'new_reservation',
                        restaurantId: restaurant.id
                    }
                }).catch(err => console.error("Error sending reservation notification:", err));
            }

            setSuccess(true);
        } catch (err) {
            console.error("Error submitting reservation:", err);
            setError("Une erreur est survenue lors de la réservation. Veuillez réessayer.");
        } finally {
            setIsLoading(false);
        }
    };

    const isFull = capacityInfo.current + formData.guests > capacityInfo.max;

    return (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6">
            <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClose}
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            
            <motion.div 
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "100%" }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className="relative w-full max-w-lg bg-white rounded-t-[3rem] sm:rounded-[3rem] shadow-2xl overflow-hidden pb-safe"
            >
                {/* Close Button */}
                <button 
                    onClick={onClose}
                    className="absolute top-6 right-6 w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-200 transition-colors z-10"
                >
                    <X size={20} />
                </button>

                <div className="p-8">
                    {!success ? (
                        <>
                            <div className="mb-8">
                                <h2 className="text-3xl font-black text-gray-900 uppercase italic tracking-tighter">Réserver une Table</h2>
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mt-1">{restaurant.name}</p>
                            </div>

                            {/* Step Indicator */}
                            <div className="flex gap-2 mb-8">
                                <div className={`h-1.5 flex-1 rounded-full ${step >= 1 ? 'bg-[#E31E24]' : 'bg-gray-100'}`} />
                                <div className={`h-1.5 flex-1 rounded-full ${step >= 2 ? 'bg-[#E31E24]' : 'bg-gray-100'}`} />
                            </div>

                            {step === 1 ? (
                                <div className="space-y-6">
                                    {/* Date & Time */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Date</label>
                                            <div className="relative">
                                                <CalendarIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                                                <input 
                                                    type="date" 
                                                    min={new Date().toISOString().split('T')[0]}
                                                    value={formData.date}
                                                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                                                    className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-100 rounded-2xl font-bold text-sm focus:bg-white focus:border-[#E31E24] transition-all outline-none"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Heure</label>
                                            <div className="relative">
                                                <ClockIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                                                <input 
                                                    type="time" 
                                                    value={formData.time}
                                                    onChange={(e) => setFormData({...formData, time: e.target.value})}
                                                    className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-100 rounded-2xl font-bold text-sm focus:bg-white focus:border-[#E31E24] transition-all outline-none"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Guests */}
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Nombre de convives</label>
                                        <div className="flex items-center gap-4 bg-gray-50 p-2 rounded-2xl border border-gray-100">
                                            <button 
                                                onClick={() => setFormData({...formData, guests: Math.max(1, formData.guests - 1)})}
                                                className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-gray-900 shadow-sm font-black text-xl active:scale-95"
                                            >
                                                -
                                            </button>
                                            <div className="flex-1 flex items-center justify-center gap-2">
                                                <Users size={18} className="text-[#E31E24]" />
                                                <span className="text-xl font-black italic">{formData.guests}</span>
                                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Personnes</span>
                                            </div>
                                            <button 
                                                onClick={() => setFormData({...formData, guests: formData.guests + 1})}
                                                className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-gray-900 shadow-sm font-black text-xl active:scale-95"
                                            >
                                                +
                                            </button>
                                        </div>
                                    </div>

                                    {/* Capacity Indicator */}
                                    <div className={`p-4 rounded-2xl flex items-center gap-3 ${isFull ? 'bg-red-50 text-red-600 border border-red-100' : 'bg-green-50 text-green-600 border border-green-100'}`}>
                                        {isFull ? <AlertTriangle size={20} /> : <CheckCircle size={20} />}
                                        <div className="flex-1">
                                            <p className="text-[10px] font-black uppercase tracking-widest">
                                                {isFull ? 'Créneau Complet' : 'Disponibilité Confirmée'}
                                            </p>
                                            <p className="text-[9px] font-bold uppercase opacity-60">
                                                {capacityInfo.max - capacityInfo.current} places restantes à {formData.time}
                                            </p>
                                        </div>
                                    </div>

                                    <button 
                                        disabled={isFull}
                                        onClick={handleNext}
                                        className="w-full py-5 bg-gray-900 text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-2xl shadow-gray-200 disabled:opacity-50 transition-all active:scale-95"
                                    >
                                        Continuer
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Votre Nom</label>
                                            <input 
                                                type="text" 
                                                value={formData.clientName}
                                                onChange={(e) => setFormData({...formData, clientName: e.target.value})}
                                                placeholder="Nom complet"
                                                className="w-full px-6 py-4 bg-gray-50 border border-gray-100 rounded-2xl font-bold text-sm focus:bg-white focus:border-[#E31E24] transition-all outline-none"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Numéro de Téléphone</label>
                                            <input 
                                                type="tel" 
                                                value={formData.clientPhone}
                                                onChange={(e) => setFormData({...formData, clientPhone: e.target.value})}
                                                placeholder="05XXXXXXXX"
                                                className="w-full px-6 py-4 bg-gray-50 border border-gray-100 rounded-2xl font-bold text-sm focus:bg-white focus:border-[#E31E24] transition-all outline-none"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Notes Particulières (Optionnel)</label>
                                            <div className="relative">
                                                <MessageSquare className="absolute left-6 top-4 text-gray-400" size={18} />
                                                <textarea 
                                                    value={formData.specialRequests}
                                                    onChange={(e) => setFormData({...formData, specialRequests: e.target.value})}
                                                    placeholder="Allergies, anniversaire, préférence de table..."
                                                    className="w-full pl-14 pr-6 py-4 bg-gray-50 border border-gray-100 rounded-2xl font-bold text-sm focus:bg-white focus:border-[#E31E24] transition-all outline-none h-32 resize-none"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {error && (
                                        <div className="p-4 bg-red-50 text-red-600 rounded-2xl flex items-center gap-3 border border-red-100">
                                            <AlertTriangle size={20} />
                                            <p className="text-[10px] font-black uppercase tracking-widest">{error}</p>
                                        </div>
                                    )}

                                    <div className="flex gap-4">
                                        <button 
                                            onClick={() => setStep(1)}
                                            className="flex-1 py-5 bg-gray-100 text-gray-600 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-gray-200 transition-colors"
                                        >
                                            Retour
                                        </button>
                                        <button 
                                            onClick={handleSubmit}
                                            disabled={isLoading}
                                            className="flex-[2] py-5 bg-[#E31E24] text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-red-200 disabled:opacity-50 transition-all active:scale-95"
                                        >
                                            {isLoading ? 'Envoi...' : 'Confirmer'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-center py-12">
                            <div className="w-24 h-24 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6 text-green-500 shadow-inner">
                                <CheckCircle size={48} />
                            </div>
                            <h2 className="text-3xl font-black text-gray-900 uppercase italic tracking-tighter mb-4">Demande Envoyée !</h2>
                            <p className="text-sm font-bold text-gray-500 leading-relaxed mb-10 max-w-xs mx-auto">
                                Votre demande de réservation a été transmise au restaurant. Vous recevrez une notification dès qu'elle sera validée.
                            </p>
                            <button 
                                onClick={onClose}
                                className="w-full py-5 bg-gray-900 text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-2xl shadow-gray-200"
                            >
                                Super, Merci !
                            </button>
                        </div>
                    )}
                </div>
            </motion.div>
        </div>
    );
};

export default ReservationModal;
